"use client";

import React, { useEffect, useState, useRef, ReactNode } from "react";
import { Box, Spinner } from "@chakra-ui/react";
import { useUserChoices } from "@/state/globalStore";
import { MindstringPrototype } from "@/generated/fastapi";
import appSettings from "@/shared/config/appSettings";

interface DeferredSearchIndexMountProps {
  prototype: MindstringPrototype;
  children: ReactNode;
  /** Optional: Enable intersection observer for viewport-based lazy loading */
  enableIntersectionObserver?: boolean;
  /** Optional: Show loading spinner while mounting */
  showLoadingSpinner?: boolean;
}

/**
 * Lazy wrapper for InstantSearch Index components
 * Only mounts children when:
 * 1. The prototype tab is active (required)
 * 2. The component is in viewport (optional with enableIntersectionObserver)
 *
 * This prevents federated search from querying all indices simultaneously
 */
export default function DeferredSearchIndexMount({
  prototype,
  children,
  enableIntersectionObserver = false,
  showLoadingSpinner = false,
}: DeferredSearchIndexMountProps) {
  const { activePrototypeCategory } = useUserChoices();
  const [isInViewport, setIsInViewport] = useState(!enableIntersectionObserver);
  const [isMounting, setIsMounting] = useState(showLoadingSpinner);
  const containerRef = useRef<HTMLDivElement>(null);

  // Check if this prototype tab is active
  const isTabActive = activePrototypeCategory === prototype;

  // Intersection observer for viewport detection
  useEffect(() => {
    if (!enableIntersectionObserver || !isTabActive) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInViewport(true);
            // Once mounted, keep it mounted (don't unmount when scrolled away)
            // This prevents losing search state
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: appSettings.search.ROOT_MARGIN,
        threshold: appSettings.search.THRESHOLD,
      },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [enableIntersectionObserver, isTabActive]);

  // Clear the initial mount spinner once the index is eligible to render.
  useEffect(() => {
    if (!showLoadingSpinner || !isTabActive || !isInViewport || !isMounting) {
      return;
    }

    const timer = setTimeout(() => setIsMounting(false), 100);
    return () => clearTimeout(timer);
  }, [showLoadingSpinner, isTabActive, isInViewport, isMounting]);

  // Only render when tab is active AND (in viewport OR intersection observer disabled)
  const shouldRender = isTabActive && isInViewport;

  if (!shouldRender) {
    return <div ref={containerRef} style={{ minHeight: "100px" }} />;
  }

  if (showLoadingSpinner && isMounting) {
    return (
      <Box ref={containerRef} display="flex" justifyContent="center" py="4">
        <Spinner size="sm" />
      </Box>
    );
  }

  return <div ref={containerRef}>{children}</div>;
}
